'use client';

import { useState } from 'react';
import { useLockFn, useMemoizedFn } from 'ahooks';
import { interactionResponse } from '@zillow/yield-callback';

/**
 * Runs `action` after the tap that called it has painted, and ignores calls while one is pending.
 * Modal.Root only yields for INP when Floating UI opens it, so a controlled `setOpen(true)` from our
 * own onClick puts the dialog render and the Backdrop scroll-lock restyle before the first paint.
 * `trackPending` re-renders the caller inside the tap, so turn it on only to paint a pressed state.
 */
export function useAfterPaint<Args extends Array<unknown>>(
  action: (...args: Args) => void,
  { trackPending = false }: { trackPending?: boolean } = {},
) {
  const latestAction = useMemoizedFn(action);
  const [isPending, setIsPending] = useState(false);

  const run = useLockFn(
    useMemoizedFn(async (...args: Args) => {
      if (trackPending) setIsPending(true);
      try {
        await interactionResponse();
        latestAction(...args);
      } finally {
        if (trackPending) setIsPending(false);
      }
    }),
  );

  return { run, isPending };
}
